import { initializeBindings } from "https://static.cloud.coveo.com/atomic/v3/index.esm.js";
import {
	buildBreadcrumbManager,
	buildSearchBox,
	loadGenericAnalyticsActions
} from "https://static.cloud.coveo.com/headless/v3/headless.esm.js";

const RECENT_SEARCH_COOKIE_NAME = "Exxon-last-searches-atomic";
const RECENT_SEARCH_COOKIE_DAYS = 10;
const RECENT_SEARCH_LIMIT = 5;

class CustomSearchBar extends HTMLElement {
	async connectedCallback() {
		this.bindings = await initializeBindings(this);
		this.breadcrumbManager = buildBreadcrumbManager(this.bindings.engine);

		this.searchBox = buildSearchBox(this.bindings.engine, {
			options: {
				numberOfSuggestions: 5,
				clearFilters: false
			}
		});

		this._suppressSuggestions = false;
		this._showSuggestionsFromTyping = false;
		this._hasSyncedRetainedSearchTerm = false;

		this.render();
		this.cacheDom();
		if (!this.hasRequiredDom()) {
			return;
		}
		this.bindEvents();
		this.renderRecentSearches();

		requestAnimationFrame(() => {
			this.syncRetainedSearchTerm();
		});

		this.searchBox.subscribe(async () => {
			await this.updateComplete;
			this.updateUI();
		});
	}

	render() {
		if (this.querySelector(":scope > .wrapper")) {
			return;
		}

		console.warn("custom-search-bar requires inline markup in page HTML (missing .wrapper)");
	}

	hasRequiredDom() {
		if (this.input && this.suggestionsContainer && this.searchButton) {
			return true;
		}

		console.warn("custom-search-bar requires #input, #suggestions, and #search elements");
		return false;
	}

	cacheDom() {
		this.input = this.querySelector("#input");
		this.suggestionsContainer = this.querySelector("#suggestions");
		this.searchButton = this.querySelector("#search");
		this.recentSearches = this.parentElement?.querySelector("#recentSearches");
		this.recentSearchesList = this.parentElement?.querySelector("#recentSearchesList");
		this.activeSuggestionIndex = -1;
	}

	bindEvents() {
		this.input.addEventListener("input", (event) => {
			const value = event.target.value || "";
			this.activeSuggestionIndex = -1;
			this._suppressSuggestions = false;
			this._showSuggestionsFromTyping = !!value.trim();
			this.searchBox.updateText(value);

			if (!value.trim()) {
				this.hideSuggestions();
			}
		});

		this.input.addEventListener("focus", () => {
			this._suppressSuggestions = false;
			this._showSuggestionsFromTyping = !!(this.input.value || "").trim();

			if (this._showSuggestionsFromTyping) {
				this.searchBox.showSuggestions();
				this.updateUI();
			}
		});

		this.input.addEventListener("keydown", (event) => {
			if (event.key === "Enter") {
				const suggestions = this.getRenderableSuggestions();
				const hasExplicitSelection =
					this.activeSuggestionIndex >= 0 &&
					!this.suggestionsContainer.classList.contains("hidden") &&
					!!suggestions[this.activeSuggestionIndex];

				if (hasExplicitSelection) {
					event.preventDefault();
					this.selectSuggestionByIndex(this.activeSuggestionIndex);
					return;
				}

				this._suppressSuggestions = true;
				this.submitSearch(this.input.value || "");
				this.hideSuggestions();
			}

			if (event.key === "Escape") {
				this.hideSuggestions();
			}

			if (event.key === "ArrowDown") {
				event.preventDefault();
				this.moveActiveSuggestion(1);
			}

			if (event.key === "ArrowUp") {
				event.preventDefault();
				this.moveActiveSuggestion(-1);
			}
		});

		this.searchButton.addEventListener("click", () => {
			this._suppressSuggestions = true;
			this.submitSearch(this.input.value || "");
			this.hideSuggestions();
		});

		if (this.recentSearchesList) {
			this.recentSearchesList.addEventListener("click", (event) => {
				const recentSearchButton = event.target.closest(".recent-search-term");

				if (!recentSearchButton) {
					return;
				}

				this.selectRecentSearch(recentSearchButton.dataset.searchTerm || "");
			});
		}

		document.addEventListener("click", (event) => {
			const path = event.composedPath();
			if (!path.includes(this)) {
				this.hideSuggestions();
			}
		});

		// Closes suggestions when focus moves away from the search bar, e.g. via Tab or Shift+Tab.
		this.addEventListener("focusout", (event) => {
			if (!this.contains(event.relatedTarget)) {
				this.hideSuggestions();
			}
		});
	}

	updateUI() {
		if (!this.searchBox.state.isLoadingSuggestions) {
			const state = this.searchBox.state;
			const stateValue = state.value || "";

			if (document.activeElement !== this.input || this.input.value !== stateValue) {
				this.input.value = stateValue;
			}

			this.renderSuggestions(state.suggestions || []);
		}
	}

	/**
	 * Syncs the search-page-title term from a retained input value.
	 */
	syncRetainedSearchTerm() {
		const retainedSearchTerm = (this.input?.value || "").trim();

		if (!retainedSearchTerm) {
			return;
		}

		this.appendSearchTermToPageTitleTag(retainedSearchTerm);
		this._hasSyncedRetainedSearchTerm = true;
	}

	renderSuggestions(mlSuggestions) {
		const inputValue = (this.input.value || "").trim();
		const suggestions = this.getRenderableSuggestions(mlSuggestions);

		if (this._suppressSuggestions || !this._showSuggestionsFromTyping) {
			this.hideSuggestions();
			return;
		}

		if (!inputValue || !suggestions.length) {
			this.hideSuggestions();
			return;
		}

		this.suggestionsContainer.replaceChildren();
		suggestions.forEach((suggestion, index) => {
			const suggestionText = suggestion.rawValue.trim();
			const suggestionElement = document.createElement("div");
			suggestionElement.id = `search-suggestion-${index}`;
			suggestionElement.className = `item${index === this.activeSuggestionIndex ? " active" : ""}`;
			suggestionElement.tabIndex = -1;
			suggestionElement.dataset.index = String(index);
			suggestionElement.setAttribute("role", "option");
			suggestionElement.setAttribute("aria-selected", index === this.activeSuggestionIndex ? "true" : "false");

			const value = document.createElement("span");
			const normalizedSuggestionText = suggestionText.toLocaleLowerCase();
			const normalizedInputValue = inputValue.toLocaleLowerCase();
			let currentIndex = 0;
			let matchIndex = normalizedSuggestionText.indexOf(normalizedInputValue);

			while (matchIndex >= 0) {
				if (matchIndex > currentIndex) {
					value.append(document.createTextNode(suggestionText.slice(currentIndex, matchIndex)));
				}

				const matchedText = document.createElement("span");
				matchedText.className = "suggestion-match";
				matchedText.textContent = suggestionText.slice(matchIndex, matchIndex + inputValue.length);
				value.append(matchedText);

				currentIndex = matchIndex + inputValue.length;
				matchIndex = normalizedSuggestionText.indexOf(normalizedInputValue, currentIndex);
			}

			if (currentIndex < suggestionText.length) {
				value.append(document.createTextNode(suggestionText.slice(currentIndex)));
			}

			suggestionElement.append(value);
			this.suggestionsContainer.append(suggestionElement);
		});

		this.suggestionsContainer.classList.remove("hidden");
		this.suggestionsContainer.removeAttribute("aria-hidden");
		this.input.setAttribute("aria-expanded", "true");
		if (this.activeSuggestionIndex >= 0) {
			this.input.setAttribute("aria-activedescendant", `search-suggestion-${this.activeSuggestionIndex}`);
		} else {
			this.input.removeAttribute("aria-activedescendant");
		}

		this.suggestionsContainer.querySelectorAll(".item").forEach((element) => {
			element.addEventListener("mousedown", (event) => {
				event.preventDefault();
			});

			element.addEventListener("click", () => {
				const index = Number(element.dataset.index);
				this.selectSuggestionByIndex(index);
			});
		});
	}

	moveActiveSuggestion(direction) {
		const suggestions = this.getRenderableSuggestions();
		if (!suggestions.length) {
			return;
		}

		const total = suggestions.length;
		const next = this.activeSuggestionIndex + direction;
		this.activeSuggestionIndex = Math.max(0, Math.min(total - 1, next < 0 ? total - 1 : next));
		this.renderSuggestions(this.searchBox.state.suggestions || []);
	}

	selectSuggestionByIndex(index) {
		const suggestions = this.getRenderableSuggestions();
		const suggestion = suggestions[index];
		const suggestionValue = suggestion ? suggestion.rawValue.trim() : "";

		if (!suggestionValue) {
			return;
		}

		this.activeSuggestionIndex = -1;
		this._suppressSuggestions = true;

		this.input.value = suggestionValue;
		this.appendSearchTermToPageTitleTag(suggestionValue);
		this.saveRecentSearch(suggestionValue);
		this.renderRecentSearches();
		this.searchBox.selectSuggestion(suggestionValue);
		this.hideSuggestions();
	}

	/**
	 * Submits a typed or selected search term and stores it when cookie consent allows it.
	 */
	submitSearch(searchTerm) {
		const query = searchTerm.trim();

		if (query) {
			this.appendSearchTermToPageTitleTag(query);
			this.saveRecentSearch(query);
			this.renderRecentSearches();
		} else {
			this.appendSearchTermToPageTitleTag("");
		}

		this.searchBox.updateText(query);
		this.searchBox.submit();
	}

	/**
	 * Appends the current search term to the search-page-title tag text.
	 */
	appendSearchTermToPageTitleTag(searchTerm) {
		const pageTitleTag = document.querySelector(".search-page-title");

		if (!pageTitleTag) {
			return;
		}

		let searchTermSpan = pageTitleTag.querySelector(".search-term");

		if (!searchTermSpan) {
			searchTermSpan = document.createElement("span");
			searchTermSpan.className = "search-term";
			pageTitleTag.appendChild(searchTermSpan);
		}

		searchTermSpan.textContent = searchTerm ? ` '${searchTerm}'` : "";
	}

	/**
	 * Runs a recent search selected from the cookie-backed recent-search list.
	 */
	selectRecentSearch(searchTerm) {
		const query = searchTerm.trim();

		if (!query) {
			return;
		}

		this._suppressSuggestions = true;
		this.input.value = query;
		this.clearFacetFiltersForRecentSearch();
		this.trackRecentSearchClick(query);
		this.submitSearch(query);
		this.hideSuggestions();
	}

	/**
	 * Clears selected facet filters before running a recent search term.
	 */
	clearFacetFiltersForRecentSearch() {
		if (!this.breadcrumbManager) {
			return;
		}
		this.breadcrumbManager.deselectAll();
	}

	/**
	 * Sends a custom analytics event when a user picks a recent search item.
	 */
	trackRecentSearchClick(searchTerm) {
		const { logCustomEvent } = loadGenericAnalyticsActions(this.bindings.engine);
		const payload = {
			evt: searchTerm,
			type: "recentSearchClick",
		};

		this.bindings.engine.dispatch(logCustomEvent(payload));
	}

	hideSuggestions() {
		this.activeSuggestionIndex = -1;
		this._showSuggestionsFromTyping = false;
		this.suggestionsContainer.classList.add("hidden");
		this.suggestionsContainer.setAttribute("aria-hidden", "true");
		this.suggestionsContainer.replaceChildren();
		this.input.setAttribute("aria-expanded", "false");
		this.input.removeAttribute("aria-activedescendant");
	}


	getRenderableSuggestions(suggestions = this.searchBox.state.suggestions || []) {
		const seenSuggestions = new Set();

		return suggestions.filter((suggestion) => {
			const suggestionValue = suggestion.rawValue?.trim();

			if (!suggestionValue) {
				return false;
			}

			const suggestionKey = suggestionValue.toLocaleLowerCase();
			if (seenSuggestions.has(suggestionKey)) {
				return false;
			}

			seenSuggestions.add(suggestionKey);
			return true;
		});
	}

	/**
	 * Reads recent searches from the existing Coveo recent-search cookie format.
	 */
	getRecentSearches() {
		const cookieValue = this.getCookie(RECENT_SEARCH_COOKIE_NAME);

		if (!cookieValue) {
			return [];
		}

		try {
			const parsedSearches = JSON.parse(decodeURIComponent(cookieValue));

			if (!Array.isArray(parsedSearches)) {
				return [];
			}

			return parsedSearches;
		} catch (error) {
			console.warn("Unable to parse recent searches cookie", error);
			return [];
		}
	}

	cleanUrl(url) {
		return url.replace(/\/$/, ""); // Removes a trailing slash if it exists
	}

	currentNormalizedUrl() {
		return this.cleanUrl(window.location.origin + window.location.pathname);
	}

	/**
	 * Stores a submitted query as the newest recent search when CoveoSearch cookies are allowed.
	 */
	saveRecentSearch(searchTerm) {
		const query = searchTerm.trim();

		if (!query || !this.hasCoveoCookieConsent()) {
			return;
		}

		const existingSearches = this.getRecentSearches();
		const recentSearchesForPage = existingSearches.find((item) => this.cleanUrl(item.pageUri) === this.currentNormalizedUrl())?.recentSearches;

		let updatedSearches;

		if (recentSearchesForPage?.length) {
			const uniqueSearches = new Set([query, ...recentSearchesForPage]);
			const limitedSearches = uniqueSearches;

			const existingPageRecordIndex = existingSearches.findIndex(
				(item) => this.cleanUrl(item.pageUri) === this.currentNormalizedUrl()
			);

			if (existingPageRecordIndex >= 0) {
				existingSearches[existingPageRecordIndex].recentSearches = Array.from(limitedSearches).slice(0, RECENT_SEARCH_LIMIT);
			}

			updatedSearches = existingSearches;
		} else {
			const newPageRecord = {
				pageUri: this.currentNormalizedUrl(),
				recentSearches: [query]
			};
			updatedSearches = [newPageRecord, ...existingSearches];
		}

		const searchesToStore = updatedSearches;

		this.setCookie(
			RECENT_SEARCH_COOKIE_NAME,
			encodeURIComponent(JSON.stringify(searchesToStore)),
			RECENT_SEARCH_COOKIE_DAYS
		);
	}

	/**
	 * Renders recent searches when cookies are accepted and stored searches exist.
	 */
	renderRecentSearches() {
		if (!this.recentSearches || !this.recentSearchesList) {
			return;
		}

		this.recentSearchesList.replaceChildren();

		if (!this.hasCoveoCookieConsent()) {
			this.hideRecentSearches();
			return;
		}

		const recentSearches = this.getRecentSearches();

		if (!recentSearches?.length) {
			this.hideRecentSearches();
			return;
		}

		const recentSearchesForPage = recentSearches.find((item) => this.cleanUrl(item.pageUri) === this.currentNormalizedUrl())?.recentSearches;

		recentSearchesForPage.forEach((recentSearch) => {
			const recentSearchItem = document.createElement("li");
			const recentSearchButton = document.createElement("button");

			recentSearchButton.type = "button";
			recentSearchButton.className = "recent-search-term";
			recentSearchButton.dataset.searchTerm = recentSearch;
			recentSearchButton.textContent = recentSearch;

			recentSearchItem.append(recentSearchButton);
			this.recentSearchesList.append(recentSearchItem);
		});

		this.recentSearches.hidden = false;
		this.recentSearches.removeAttribute("aria-hidden");
		this.recentSearches.classList.remove("hidden");
	}

	/**
	 * Hides the recent-search UI and clears its status flag.
	 */
	hideRecentSearches() {
		this.recentSearches.hidden = true;
		this.recentSearches.setAttribute("aria-hidden", "true");
		this.recentSearches.classList.add("hidden");
	}

	/**
	 * Gets a raw cookie value by name without exposing unrelated cookies.
	 */
	getCookie(cookieName) {
		const cookiePrefix = `${cookieName}=`;
		const cookies = document.cookie.split(";");

		for (const cookie of cookies) {
			const trimmedCookie = cookie.trim();

			if (trimmedCookie.startsWith(cookiePrefix)) {
				return trimmedCookie.substring(cookiePrefix.length);
			}
		}

		return "";
	}

	/**
	 * Sets a same-path recent-search cookie with a bounded retention window.
	 */
	setCookie(cookieName, cookieValue, daysToExpire) {
		const expiresAt = new Date();
		expiresAt.setTime(expiresAt.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
		document.cookie = `${cookieName}=${cookieValue};expires=${expiresAt.toUTCString()};path=/;SameSite=Lax`;

		if (!this.getCookie(cookieName)) {
			console.warn(`Unable to write ${cookieName}; the browser may be blocking cookies for this local context.`);
		}
	}

	/**
	 * Checks the existing consent manager category used by Coveo search cookies.
	 */
	hasCoveoCookieConsent() {
		// uncomment the following lines to enable local recent search test mode
		// if (this.isLocalRecentSearchTestMode()) {
		// 	return true;
		// }

		if (window.consentManager === undefined) {
			return true;
		}

		return window.consentManager.isCategoryActive("CoveoSearch");
	}

	//uncomment the following lines to enable local recent search test mode
	// isLocalRecentSearchTestMode() {
	// 	return window.location.protocol === "file:" || ["localhost", "127.0.0.1"].includes(window.location.hostname);
	// }
}

if (!window.customElements.get("custom-search-bar")) {
	window.customElements.define("custom-search-bar", CustomSearchBar);
}
